import React, { PropsWithChildren, useEffect } from 'react';
import { logger } from '../../common/logger';
import {
  ErrorName,
  LoginApiError5xx,
  SilentError,
  UnknownError,
} from './errors';
import { Language } from '@yleisradio/areena-types';
import { errorMessages } from '../localizations/translations';
import { ErrorWithMessage } from './errorMessages';
import useLoginApiError from './useLoginApiError';
import { useErrorContext } from './ErrorBoundaryContext';
import {
  logUnexpectedError,
  logVisibleErrorToApm,
  recoverErroneousSession,
} from './errorUtils';
import { useProfilesLocalStorage } from '../ProfilesLocalStorageContext';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext/NotificationContext';

type Props = PropsWithChildren;

/**
 * Consumes and handles errors one at the time. Does not do any redirections:
 * those are handled in useAreenaRedirects.
 * @param children
 * @constructor
 */
const ErrorConsumer: React.FC<Props> = ({ children }) => {
  const { markErrorHandled, errorToHandle } = useErrorContext();

  const { languageCode } = useAppFeaturesContext();
  const { clearSession } = useAuthContext();
  const { recoverFromLoginApiServerError5xx, flagLoginApiErrorForSession } =
    useLoginApiError();
  const { deleteProfileByCookie } = useProfilesLocalStorage();
  const { pushNotification: showNotification } = useNotificationContext();

  function handleError(error: unknown) {
    logger.debug('ErrorConsumer handling an error');

    if (error instanceof SilentError) {
      logger.debug(error);

      return;
    }

    logger.error(error);

    if (!(error instanceof Error)) {
      logUnexpectedError(error);

      const notificationMessage = getErrorMessage(
        ErrorName.ERROR,
        languageCode
      );

      if (notificationMessage) {
        showNotification({
          message: notificationMessage,
          id: 'ERROR',
        });
      }

      return;
    }

    recoverErroneousSession({
      error,
      recoverLoginApiError: (error: LoginApiError5xx) => {
        flagLoginApiErrorForSession();
        recoverFromLoginApiServerError5xx(error.sessionCookie);
      },
      recoverAuthError: clearSession,
      recoverExpiredSessionError: error => {
        clearSession();
        if (!error.sessionCookie) {
          return;
        }

        deleteProfileByCookie(error.sessionCookie);
      },
    });

    logVisibleErrorToApm(error);

    if (error instanceof UnknownError) {
      logUnexpectedError(error);
    }

    const notificationMessage = getErrorMessage(error.name, languageCode);

    if (notificationMessage) {
      showNotification({
        message: notificationMessage,
        id: error.name,
      });
    }
  }

  useEffect(() => {
    if (errorToHandle) {
      handleError(errorToHandle);
      markErrorHandled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorToHandle]);

  return children;
};

export const getErrorMessage = (
  errorName: string,
  language: Language
): string | undefined => {
  return errorMessages[errorName as ErrorWithMessage]?.[language];
};

export default ErrorConsumer;
